import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ContentArea from './components/ContentArea';
import Settings from './components/Settings';
import WelcomeScreen from './components/WelcomeScreen';
import Notification from './components/Notification';
import StudyTimer from './components/StudyTimer';
import StudyNotes from './components/StudyNotes';
import ProgressTracker from './components/ProgressTracker';
import PresentationMode from './components/PresentationMode';
import storage from './utils/storage';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('welcome');
  const [verseModal, setVerseModal] = useState(null);
  const [studyTime, setStudyTime] = useState(0);
  const [studyGuide, setStudyGuide] = useState({
    title: '',
    sections: [],
    settings: {
      theme: 'default',
      fontSize: 'medium',
      translation: 'KJV'
    },
    lastModified: new Date(),
    isModified: false
  });
  const [settings, setSettings] = useState({
    theme: 'default',
    fontSize: 'medium',
    defaultTranslation: 'KJV',
    autoSave: true,
    autoSaveInterval: 30000, // 30 seconds
    showVerseNumbers: true,
    highlightKeywords: true
  });
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [presentationMode, setPresentationMode] = useState(false);

  useEffect(() => {
    // Load settings from storage
    const loadSettings = async () => {
      try {
        const loadedSettings = await storage.loadSettings();
        if (loadedSettings) {
          setSettings(loadedSettings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();

    // Set up keyboard shortcuts
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'n':
            e.preventDefault();
            handleNewStudyGuide();
            break;
          case 'o':
            e.preventDefault();
            handleOpenStudyGuide();
            break;
          case 's':
            e.preventDefault();
            handleSaveStudyGuide();
            break;
          case ',':
            e.preventDefault();
            setCurrentView('settings');
            break;
          case 'F5':
            e.preventDefault();
            handleStartPresentation();
            break;
          case 'Escape':
            if (presentationMode) {
              e.preventDefault();
              handleExitPresentation();
            }
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (autoSaveTimer) {
        clearInterval(autoSaveTimer);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Separate effect for auto-save timer
  useEffect(() => {
    if (autoSaveTimer) {
      clearInterval(autoSaveTimer);
    }

    if (settings.autoSave && settings.autoSaveInterval > 0) {
      const timer = setInterval(() => {
        if (studyGuide.isModified) {
          handleAutoSave();
        }
      }, settings.autoSaveInterval);
      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearInterval(autoSaveTimer);
      }
    };
  }, [settings.autoSave, settings.autoSaveInterval, studyGuide.isModified]);

  const handleNewStudyGuide = useCallback(() => {
    setStudyGuide({
      title: '',
      sections: [],
      settings: { ...settings },
      lastModified: new Date(),
      isModified: false
    });
    setCurrentView('main');
  }, [settings]);

  const handleGetStarted = useCallback(() => {
    setCurrentView('main');
  }, []);

  const showNotification = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type, duration }]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration + 300); // Add extra time for fade out
  }, []);

  const handleOpenStudyGuide = useCallback(async () => {
    try {
      // For web version, we'll show a list of saved study guides
      const savedGuides = await storage.listStudyGuides();

      if (savedGuides.length === 0) {
        showNotification('No saved study guides found. Create a new one to get started!', 'info');
        return;
      }

      // For now, open the most recent guide
      // In a full implementation, you'd show a selection dialog
      const mostRecentGuide = savedGuides[savedGuides.length - 1];

      if (mostRecentGuide) {
        setStudyGuide({
          ...mostRecentGuide,
          isModified: false,
          lastModified: new Date(mostRecentGuide.lastModified) || new Date()
        });
        setCurrentView('main');
        showNotification('Study guide opened successfully!', 'success');
      } else {
        showNotification('Error opening study guide. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error opening guide:', error);
      showNotification('Error opening study guide. Please try again.', 'error');
    }
  }, [showNotification]);

  const handleAutoSave = useCallback(async () => {
    try {
      if (!studyGuide.isModified) return;

      const guideToSave = {
        ...studyGuide,
        lastModified: new Date(),
        isModified: false
      };

      const result = await storage.saveStudyGuide(guideToSave);

      if (result && result.success !== false) {
        console.log('Auto-saved successfully');
        setStudyGuide(prev => ({ ...prev, isModified: false, lastModified: new Date() }));
        showNotification('Study guide auto-saved', 'success', 2000);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      showNotification('Auto-save failed', 'error');
    }
  }, [studyGuide, showNotification]);

  const handleSaveStudyGuide = useCallback(async () => {
    try {
      const guideToSave = {
        ...studyGuide,
        lastModified: new Date(),
        isModified: false
      };

      const result = await storage.saveStudyGuide(guideToSave);

      if (result && result.success !== false) {
        setStudyGuide(prev => ({ ...prev, isModified: false, lastModified: new Date() }));
        showNotification('Study guide saved successfully!', 'success');
      } else {
        showNotification('Error saving study guide: ' + (result?.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error saving study guide:', error);
      showNotification('Error saving study guide. Please try again.', 'error');
    }
  }, [studyGuide, showNotification]);

  const handleExportPDF = useCallback(async () => {
    try {
      const { exportToPDF } = await import('./utils/exportUtils');
      await exportToPDF(studyGuide);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showNotification('Error exporting PDF. Please try again.', 'error');
    }
  }, [studyGuide]);

  const handleExportWord = useCallback(async () => {
    try {
      const { exportToWord } = await import('./utils/exportUtils');
      exportToWord(studyGuide);
    } catch (error) {
      console.error('Error exporting Word:', error);
      showNotification('Error exporting Word document. Please try again.', 'error');
    }
  }, [studyGuide]);

  const handleStartPresentation = useCallback(() => {
    if (studyGuide.sections && studyGuide.sections.length > 0) {
      setPresentationMode(true);
    } else {
      showNotification('Please add some content to your study guide before starting presentation mode.', 'warning');
    }
  }, [studyGuide, showNotification]);

  const handleExitPresentation = useCallback(() => {
    setPresentationMode(false);
  }, []);

  const updateStudyGuide = useCallback((updates) => {
    setStudyGuide(prev => ({
      ...prev,
      ...updates,
      isModified: true,
      lastModified: new Date()
    }));
  }, []);

  const updateSettings = useCallback(async (newSettings) => {
    setSettings(newSettings);
    try {
      await storage.saveSettings(newSettings);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }, []);

  const openVerseModal = useCallback((book, chapter, verses, translation) => {
    setVerseModal({ book, chapter, verses, translation });
  }, []);

  const closeVerseModal = useCallback(() => {
    setVerseModal(null);
  }, []);

  const addVerseToGuide = useCallback((verse) => {
    const newSection = {
      id: Date.now(),
      type: 'verse',
      book: verseModal.book,
      chapter: verseModal.chapter,
      verse: verse.verse,
      text: verse.text,
      notes: '',
      questions: [],
      observations: ''
    };

    updateStudyGuide({
      sections: [...studyGuide.sections, newSection]
    });
    closeVerseModal();
  }, [verseModal, updateStudyGuide, closeVerseModal]);

  const themeClass = `theme-${settings.theme || 'default'}`;

  return (
    <div className={`app ${themeClass}`} data-theme={settings.theme || 'default'}>
      {currentView === 'welcome' ? (
        <WelcomeScreen
          onGetStarted={handleGetStarted}
          onOpenExisting={handleOpenStudyGuide}
        />
      ) : (
        <>
          <Header
            onNewGuide={handleNewStudyGuide}
            onOpenGuide={handleOpenStudyGuide}
            onSaveGuide={handleSaveStudyGuide}
            onExportPDF={handleExportPDF}
            onExportWord={handleExportWord}
            onOpenSettings={() => setCurrentView('settings')}
            onStartPresentation={handleStartPresentation}
            studyGuide={studyGuide}
          />
          <div className="main-content">
            {currentView === 'main' ? (
              <>
                <Sidebar
                  studyGuide={studyGuide}
                  updateStudyGuide={updateStudyGuide}
                  settings={settings}
                  onOpenVerseModal={openVerseModal}
                />
                <div className="content-and-tools">
                  <ContentArea studyGuide={studyGuide} updateStudyGuide={updateStudyGuide} settings={settings} />
                  <div className="study-tools">
                    <StudyTimer onTimeUpdate={setStudyTime} />
                    <ProgressTracker studyGuide={studyGuide} studyTime={studyTime} />
                    <StudyNotes studyGuide={studyGuide} updateStudyGuide={updateStudyGuide} />
                  </div>
                </div>
              </>
            ) : (
              <Settings
                settings={settings}
                updateSettings={updateSettings}
                onClose={() => setCurrentView('main')}
              />
            )}
          </div>
        </>
      )}

      {/* File manager removed - using direct file operations now */}

      {verseModal && (
        <div className="modal verse-main-modal">
          <div className="modal-content verse-modal-large">
            <div className="modal-header">
              <h2>{verseModal.book} {verseModal.chapter} ({verseModal.translation})</h2>
              <button className="close-btn" onClick={closeVerseModal}>×</button>
            </div>
            <div className="verse-modal-content">
              {verseModal.verses.map(verse => (
                <div key={verse.verse} className="verse-modal-item">
                  <div className="verse-display">
                    <div className="verse-reference">
                      {verseModal.book} {verseModal.chapter}:{verse.verse}
                    </div>
                    <div className="verse-text">{verse.text}</div>
                  </div>
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => addVerseToGuide(verse)}
                    title="Add to study guide"
                  >
                    Add to Guide
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Presentation Mode */}
      {presentationMode && (
        <PresentationMode
          studyGuide={studyGuide}
          onExit={handleExitPresentation}
        />
      )}

      {/* Notification System */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
          />
        ))}
      </div>
    </div>
  );
}

export default App;